import { z } from 'zod';

// =============================================================================
// Discord Linked Roles — tier/cycle/admin metadata pushed to Discord
// =============================================================================

export const discordTier = z.enum(['free', 'pro', 'pro_plus']);
export type DiscordTier = z.infer<typeof discordTier>;

export const discordBillingCycle = z.enum(['monthly', 'annual']);
export type DiscordBillingCycle = z.infer<typeof discordBillingCycle>;

export const discordMetadata = z.object({
  tier: discordTier,
  billing_cycle: discordBillingCycle,
  is_admin: z.boolean(),
});
export type DiscordMetadata = z.infer<typeof discordMetadata>;

// =============================================================================
// GET /api/discord/oauth/start
// =============================================================================

export const discordOAuthStartResponse = z.object({
  url: z.string().url(),
});
export type DiscordOAuthStartResponse = z.infer<typeof discordOAuthStartResponse>;

// =============================================================================
// POST /api/discord/oauth/finalize
// =============================================================================

export const discordOAuthFinalizeBody = z.object({
  code: z.string().min(1, 'code is required'),
  state: z.string().min(1, 'state is required'),
});
export type DiscordOAuthFinalizeBody = z.infer<typeof discordOAuthFinalizeBody>;

/**
 * Finalize returns 200 in two shapes:
 *   - `{ ok: true, … }` — link stored + metadata pushed to Discord successfully
 *   - `{ ok: false, push_error, metadata }` — link stored, but Discord rejected
 *     the metadata push. The link persists; the next admin-grant change or
 *     Stripe webhook will retry. Frontend renders an error variant.
 *
 * 4xx responses indicate the link was NOT stored (bad_state, user_mismatch,
 * etc.) and use the standard ApiErrorResponse shape.
 */
export const discordOAuthFinalizeResponse = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    discord_user_id: z.string(),
    metadata: discordMetadata,
  }),
  z.object({
    ok: z.literal(false),
    push_error: z.literal('discord_unreachable'),
    metadata: discordMetadata,
  }),
]);
export type DiscordOAuthFinalizeResponse = z.infer<typeof discordOAuthFinalizeResponse>;

// =============================================================================
// POST /api/discord/disconnect
// =============================================================================

export const discordDisconnectResponse = z.object({
  ok: z.literal(true),
});
export type DiscordDisconnectResponse = z.infer<typeof discordDisconnectResponse>;
